import { ChequeGiroModel } from 'api-hooks/cheque-giro';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import { OnChangeParams } from 'baseui/select';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import BankAccountSelectInput from 'legacy-modules/master/bank-account/components/select-input';
import CashAccountSelectInput from 'legacy-modules/master/cash-account/components/select-input';
import ChequeGiroSelectInput from 'legacy-modules/master/cheque-giro/components/select-input';
import PaymentMethodSelectInput from 'legacy-modules/master/select-input/payment-method-select-input';
import { HalfContainer, SectionContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {}

export default function CustomerPaymentField(props: Props) {
  const { t } = useTranslation();
  const { setValue, getValues, clearErrors, control } = useFormContext();
  const { editable } = useFormState();

  const onChangePaymentMethod = React.useCallback(
    (params: OnChangeParams | undefined) => {
      const value = params?.option;
      if (editable) {
        const customerId =
          value?.type === PaymentMethodEnum.Balance
            ? getValues('releaseSale.customerId')
            : null;
        setValue('releaseSale.chequeNote', null);
        setValue('releaseSale.paymentableId', customerId);
      }
      if (params) {
        setValue('releaseSale.paymentMethod', value?.type);
        setValue('releaseSale.paymentMethodId', value?.id);
      }
      clearErrors();
    },
    [clearErrors, editable, getValues, setValue],
  );

  const onChangeChequeGiro = React.useCallback(
    ({ id }: ChequeGiroModel) => {
      setValue('releaseSale.chequeNote.chequeGiroId', id);
      setValue('releaseSale.paymentableId', id);
    },
    [setValue],
  );

  const [paymentMethod] = useWatch({
    control,
    name: ['releaseSale.paymentMethod'],
  });

  return (
    <>
      <SectionContainer>
        <HalfContainer>
          <PaymentMethodSelectInput
            name="releaseSale.paymentMethodId"
            label={t('debt:payment_method')}
            placeholder={t('debt:payment_method')}
            onAfterChange={onChangePaymentMethod}
            required
          />
        </HalfContainer>
        <HalfContainer>
          {(() => {
            switch (paymentMethod) {
              case PaymentMethodEnum.ChequeGiro:
                return (
                  <ChequeGiroSelectInput
                    name="releaseSale.chequeNote.chequeGiroId"
                    required
                    onAfterDetailChange={onChangeChequeGiro}
                  />
                );
              case PaymentMethodEnum.Bank:
                return (
                  <BankAccountSelectInput
                    name="releaseSale.paymentableId"
                    required
                  />
                );
              case PaymentMethodEnum.Cash:
                return (
                  <CashAccountSelectInput
                    name="releaseSale.paymentableId"
                    required
                  />
                );
              default:
                return <></>;
            }
          })()}
        </HalfContainer>
      </SectionContainer>

      {(() => {
        switch (paymentMethod) {
          case PaymentMethodEnum.ChequeGiro:
            return (
              <>
                <SectionContainer>
                  <HalfContainer>
                    <SectionContainer>
                      <HalfContainer>
                        <Input
                          type="text"
                          name="releaseSale.chequeNote.referenceNumber"
                          label={t('debt:reference_number')}
                          placeholder={t('debt:reference_number')}
                          required
                        />
                      </HalfContainer>
                      <HalfContainer>
                        <Input
                          type="date-time"
                          name="releaseSale.chequeNote.issuedAt"
                          label={t('debt:issued_at')}
                          placeholder={t('debt:issued_at')}
                          required
                        />
                      </HalfContainer>
                    </SectionContainer>
                  </HalfContainer>
                  <HalfContainer>
                    <SectionContainer>
                      <HalfContainer>
                        <Input
                          type="date-time"
                          name="releaseSale.chequeNote.availableAt"
                          label={t('debt:available_at')}
                          placeholder={t('debt:available_at')}
                          required
                        />
                      </HalfContainer>
                      <HalfContainer>
                        <Input
                          type="date-time"
                          name="releaseSale.chequeNote.expiredAt"
                          label={t('debt:expiry_at')}
                          placeholder={t('debt:expiry_at')}
                          required
                        />
                      </HalfContainer>
                    </SectionContainer>
                  </HalfContainer>
                </SectionContainer>

                <BankAccountSelectInput
                  name="releaseSale.chequeNote.bankAccountId"
                  required
                />
              </>
            );
          default:
            return <></>;
        }
      })()}
    </>
  );
}
